import { applyCouponDiscount } from '@/entities/reservation'
import { PLACE_DETAIL_CANONICAL_BASE } from '@/features/seoPlaceDetail/lib/seo'
import {
  type IProductGroup,
  type ISeoPlace,
} from '@/features/seoPlaceDetail/model'
import { APPLE_ITUNES_APP_ID, truncateText } from '@/shared/lib'

import { parsePrice } from './parsePrice'
import { sanitizeHttpsUrl } from './sanitize'

import type { Metadata } from 'next'

const ENGINE_OIL_SECTION = 'oil_set'
const PLACE_DESCRIPTION_TAIL =
  '타이어·배터리·에어컨필터 등 마이클에서 비용을 미리 확인하고 오늘 바로 예약하세요.'
// 검색결과에서 잘리지 않도록 title/description 최대 길이 제한
const TITLE_MAX_LENGTH = 60
const DESCRIPTION_MAX_LENGTH = 160

interface IBuildPlaceMetadataArgs {
  place: ISeoPlace
  productGroups: IProductGroup[]
  hashId: string
}

// 법정동 주소의 마지막 동/읍/면/리 토큰을 추출한다. (없으면 빈 문자열)
const extractDong = (legalDistrictAddress: string | null | undefined) => {
  if (!legalDistrictAddress) return ''
  const tokens = legalDistrictAddress.trim().split(/\s+/)
  const dongToken = [...tokens]
    .reverse()
    .find(token => /[동읍면리]$/.test(token))
  return dongToken ?? ''
}

const joinTokens = (tokens: Array<string | undefined | null>) =>
  tokens.filter((token): token is string => Boolean(token)).join(' ')

// 광역 지자체 정식 명칭을 검색결과에 자연스러운 축약형으로 변환한다.
// 예: 서울특별시→서울시, 부산광역시→부산시, 세종특별자치시→세종시,
//     강원특별자치도→강원도, 제주특별자치도→제주도 (경기도·충청북도 등은 그대로)
const shortenState = (state: string) =>
  state
    .replace(/(특별자치시|특별시|광역시)$/, '시')
    .replace(/특별자치도$/, '도')

export const buildPlaceMetadata = ({
  place,
  productGroups,
  hashId,
}: IBuildPlaceMetadataArgs): Metadata => {
  const placeName = place.placeName
  const state = shortenState(place.coordinates?.state ?? '')
  const city = place.coordinates?.city ?? ''
  const dong = extractDong(place.coordinates?.legalDistrictAddress)
  const reviewCount = place.statistic?.reviewCount ?? 0
  const engineOilProduct = productGroups.find(
    g => g.section === ENGINE_OIL_SECTION
  )?.lowestPriceTargetProduct
  // 검색 노출 가격은 정가, 쿠폰적용가 계산은 특가(salePrice) 기준을 유지
  const engineOilPrice = parsePrice(engineOilProduct?.price)
  const engineOilSalePrice = parsePrice(engineOilProduct?.salePrice)
  const engineOilCouponPrice = applyCouponDiscount(
    ENGINE_OIL_SECTION,
    engineOilSalePrice
  )

  // 지역 있음: {시} {구} {동} 엔진오일 타이어 교환 | {정비소명}
  // 지역 없음: 엔진오일 타이어 교환 | {정비소명}
  const titleRegion = joinTokens([state, city, dong])
  const rawTitle = titleRegion
    ? `${titleRegion} 엔진오일 타이어 교환 | ${placeName}`
    : `엔진오일 타이어 교환 | ${placeName}`
  const title = truncateText(rawTitle, TITLE_MAX_LENGTH)

  // {시} {구} {동} 추천 정비소, {정비소명} 후기 {N}건 — 엔진오일 {최저가}원부터, 타이어·배터리·...
  const descriptionRegion = joinTokens([state, city, dong])
  const placeClause = [
    descriptionRegion ? `${descriptionRegion} 추천 정비소` : '추천 정비소',
    reviewCount > 0
      ? `${placeName} 후기 ${reviewCount.toLocaleString()}건`
      : placeName,
  ].join(', ')

  const priceClause =
    engineOilPrice > 0
      ? `엔진오일 ${engineOilPrice.toLocaleString()}원부터(쿠폰 적용 시 ${engineOilCouponPrice.toLocaleString()}원), `
      : ''
  const description = truncateText(
    `${placeClause} — ${priceClause}${PLACE_DESCRIPTION_TAIL}`,
    DESCRIPTION_MAX_LENGTH
  )

  const imageUrl = sanitizeHttpsUrl(
    place.images?.[0]?.url || place.logoImage?.url
  )
  const canonical = `${PLACE_DETAIL_CANONICAL_BASE}/${encodeURIComponent(hashId)}`

  return {
    title,
    description,
    alternates: {
      canonical,
    },
    openGraph: {
      title,
      description,
      url: canonical,
      locale: 'ko_KR',
      type: 'website',
      images: imageUrl ? [imageUrl] : undefined,
    },
    twitter: {
      card: 'summary_large_image',
      title,
      description,
      images: imageUrl ? [imageUrl] : undefined,
    },
    other: {
      'apple-itunes-app': `app-id=${APPLE_ITUNES_APP_ID}`,
    },
  }
}
